/**
 * Fixed Asset Service (UI-18) — BE-18 (`FixedAssetController`). Real backend.
 * Returns `{ success, data }` with camelCase data, the shape the pages use.
 * Buying posts the purchase from a bank / cash / e-wallet account or a credit card; run, dispose, void and the
 * settings change are the TC admin's.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList } from '../../utils/apiConvert';

const ok = (data) => ({ success: true, data });
const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));

export const fixedAssetService = {
  // params: { status }
  getAll: async (params) => ok(unwrapList(await ApiService.get('/api/fixed-assets', { params: clean(params) })).items),

  // → { totalCost, totalBookValue, monthlyDepreciation, activeCount, fullyDepreciatedCount }
  getSummary: async () => ok(unwrapData(await ApiService.get('/api/fixed-assets/summary'))),

  getById: async (id) => ok(unwrapData(await ApiService.get(`/api/fixed-assets/${id}`))),

  // data: { name, category, purchaseDate, cost, description, paymentAccountId | paymentCardId, idempotencyKey }
  create: async (data) => ok(unwrapData(await ApiService.post('/api/fixed-assets', keysCamelToSnake(clean(data))))),

  getConfig: async () => ok(unwrapData(await ApiService.get('/api/settings/asset-config'))),

  // config: { capitalizationThreshold, categories: [{ key, usefulLifeYears }] } — only what is sent changes
  saveConfig: async (config) => ok(unwrapData(await ApiService.put('/api/settings/asset-config', keysCamelToSnake(config)))),

  // data: { disposalMethod: SOLD | SCRAPPED, disposalDate, proceedsAmount, disposalAccountId (SOLD), remarks }
  dispose: async (id, data) => ok(unwrapData(await ApiService.post(`/api/fixed-assets/${id}/dispose`, keysCamelToSnake(clean(data))))),

  voidAsset: async (id, reason) => ok(unwrapData(await ApiService.post(`/api/fixed-assets/${id}/void`, { reason }))),

  // upTo: a month that has ended, e.g. '2026-09' → { upTo, alreadyPosted, assets: [{ assetId, assetName, cost, fromMonth,
  // toMonth, months, depreciationAmount }], months: [{ month, entryDate, amount, locked }], totalDepreciation }
  getDepreciationPreview: async (upTo) =>
    ok(unwrapData(await ApiService.get('/api/fixed-assets/depreciation-preview', { params: { up_to: upTo } }))),

  // data: { upTo } → { upTo, assetsProcessed, monthsPosted, totalDepreciation, entries: [{ month, entryDate, amount, journalEntryId }] }
  runDepreciation: async (data) => ok(unwrapData(await ApiService.post('/api/fixed-assets/run-depreciation', keysCamelToSnake(data)))),

  // → { cost, usefulLifeYears, monthlyDepreciation, schedule: [{ year, months, amount, accumulated, bookValue, postedMonths, posted, journalEntryId }] }
  getDepreciationSchedule: async (id) => ok(unwrapData(await ApiService.get(`/api/fixed-assets/${id}/depreciation-schedule`))),
};

export default fixedAssetService;
